import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Plus, Search, Server } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Spinner,
  useToast,
  type SelectOption,
} from '../../ui';
import { useServices } from '../../features/services/useServices';
import {
  collectPocs,
  collectTeams,
  filterServices,
} from '../../features/services/serviceModel';
import type { ServiceFilter, ServiceSummary } from '../../features/services/types';
import { ServiceCard } from './ServiceCard';
import { ServiceModal } from './ServiceModal';

const EMPTY_FILTER: ServiceFilter = { owningTeam: '', poc: '', query: '' };

/**
 * Service Directory — "who knows this system, and where do I start?". Register a
 * service (owner, POCs, repos, and the goals / workstreams / docs / decisions
 * connected to it), then filter by team / POC or search.
 */
export function ServicesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    services,
    goals,
    workstreams,
    documents,
    decisions,
    currentAlias,
    goalTitles,
    workstreamTitles,
    documentTitles,
    decisionTitles,
    loading,
    error,
    create,
    remove,
  } = useServices();

  const [filter, setFilter] = useState<ServiceFilter>(EMPTY_FILTER);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ServiceSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const teamOptions: SelectOption[] = useMemo(
    () => collectTeams(services).map((v) => ({ value: v, label: v })),
    [services],
  );
  const pocOptions: SelectOption[] = useMemo(
    () => collectPocs(services).map((v) => ({ value: v, label: v })),
    [services],
  );

  const filtered = useMemo(() => filterServices(services, filter), [services, filter]);
  const filtersActive = !!filter.owningTeam || !!filter.poc || !!filter.query;

  const setField = <K extends keyof ServiceFilter>(key: K, value: ServiceFilter[K]) =>
    setFilter((f) => ({ ...f, [key]: value }));

  const onSubmit = async (draft: Parameters<typeof create>[0]) => {
    await create(draft);
    toast({ tone: 'success', title: 'Service registered', description: draft.name });
  };
  const onDelete = (s: ServiceSummary) => setPendingDelete(s);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await remove(pendingDelete.id);
      toast({ tone: 'success', title: 'Service removed', description: pendingDelete.name });
      setPendingDelete(null);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Could not remove service',
        description: e instanceof Error ? e.message : 'Delete failed.',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        icon={<Server className="h-5 w-5" />}
        title="Service Directory"
        description="Who owns each system, where the code lives, and everything connected to it."
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
            Register service
          </Button>
        }
      />

      <div className="mb-4">
        <Input
          placeholder="Search name, purpose, repos, dependencies…"
          value={filter.query}
          onChange={(e) => setField('query', e.target.value)}
          className="pl-9"
        />
        <Search className="pointer-events-none -mt-[26px] ml-3 h-4 w-4 text-zinc-500" aria-hidden="true" />
      </div>

      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="w-52">
          <Select label="Owning team" placeholder="All teams" value={filter.owningTeam} options={teamOptions} onChange={(e) => setField('owningTeam', e.target.value)} />
        </div>
        <div className="w-52">
          <Select label="POC" placeholder="All POCs" value={filter.poc} options={pocOptions} onChange={(e) => setField('poc', e.target.value)} />
        </div>
        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={() => setFilter(EMPTY_FILTER)}>
            Clear filters
          </Button>
        )}
        <span className="ml-auto self-center text-xs text-zinc-500">
          {filtered.length} of {services.length} {services.length === 1 ? 'service' : 'services'}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading services" />
        </div>
      ) : error ? (
        <EmptyState icon={<AlertTriangle className="h-6 w-6" />} title="Couldn't load services" description={error} />
      ) : services.length === 0 ? (
        <EmptyState
          icon={<Server className="h-6 w-6" />}
          title="No services registered"
          description="Register the systems your team owns so anyone can find the owner, the code, and the context in one place."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
              Register service
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState icon={<Server className="h-6 w-6" />} title="No services match" description="Try clearing filters or broadening your search." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((s) => (
            <ServiceCard
              key={s.id}
              service={s}
              onOpen={(id) => navigate(`/services/${encodeURIComponent(id)}`)}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      <ServiceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultPoc={currentAlias}
        goals={goals}
        workstreams={workstreams}
        documents={documents}
        decisions={decisions}
        goalTitles={goalTitles}
        workstreamTitles={workstreamTitles}
        documentTitles={documentTitles}
        decisionTitles={decisionTitles}
        onSubmit={onSubmit}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove service?"
        confirmLabel="Remove service"
        loading={deleting}
        onConfirm={() => void confirmDelete()}
        onClose={() => setPendingDelete(null)}
        message={
          <>
            Remove{' '}
            <span className="font-medium text-zinc-100">{pendingDelete?.name}</span>{' '}
            from the service directory? This can't be undone.
          </>
        }
      />
    </div>
  );
}

export default ServicesPage;
